<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import ConversationApi from 'dashboard/api/inbox/conversation';
import KanbanColumnsApi from 'dashboard/api/kanbanColumns';
import wootConstants from 'dashboard/constants/globals';
import { KANBAN_DEFAULT_SORT, KANBAN_PAGE_SIZE } from '../constants';
import KanbanColumn from '../components/KanbanColumn.vue';
import KanbanFilterBar from '../components/KanbanFilterBar.vue';
import KanbanColumnForm from '../components/KanbanColumnForm.vue';
import KanbanCardDialog from '../components/KanbanCardDialog.vue';
import KanbanCardMenu from '../components/KanbanCardMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const store = useStore();

const assigneeType = ref(wootConstants.ASSIGNEE_TYPE.ALL);
const inboxId = ref(null);
const teamId = ref(null);
const label = ref('');
const sortBy = ref(KANBAN_DEFAULT_SORT);
const searchQuery = ref('');
const isRefreshing = ref(false);
const boardGeneration = ref(0);

// Colunas vindas do backend. A coluna virtual 'none' agrupa o que ainda nao
// foi triado, para que nenhuma conversa fique invisivel no board.
const UNASSIGNED = { id: 'none', name: null, color: '#8B8F96', position: -1 };
const columnsMeta = ref([]);
const editingColumn = ref(null);
const selectedCard = ref(null);
const contextMenu = ref(null);
const router = useRouter();
const route = useRoute();
const isFormOpen = ref(false);

const boardColumns = computed(() => [UNASSIGNED, ...columnsMeta.value]);

const cards = reactive({});
const pages = reactive({});
const hasMore = reactive({});
const loading = reactive({});

const resetBuckets = () => {
  boardColumns.value.forEach(column => {
    if (!(column.id in cards)) cards[column.id] = [];
    if (!(column.id in pages)) pages[column.id] = 1;
    if (!(column.id in hasMore)) hasMore[column.id] = false;
    if (!(column.id in loading)) loading[column.id] = false;
  });
};

const fetchColumn = async (columnId, { append = false, generation } = {}) => {
  const requestGeneration = generation ?? boardGeneration.value;
  const page = append ? pages[columnId] + 1 : 1;
  loading[columnId] = true;
  try {
    const response = await ConversationApi.get({
      inboxId: inboxId.value || undefined,
      teamId: teamId.value || undefined,
      labels: label.value ? [label.value] : undefined,
      status: 'all',
      kanbanColumnId: columnId,
      assigneeType: assigneeType.value,
      page,
      sortBy: sortBy.value,
    });
    if (requestGeneration !== boardGeneration.value) return;
    const list = response.data?.data?.payload || [];
    cards[columnId] = append ? [...cards[columnId], ...list] : list;
    pages[columnId] = page;
    hasMore[columnId] = list.length >= KANBAN_PAGE_SIZE;
  } catch (error) {
    if (requestGeneration !== boardGeneration.value) return;
    useAlert(t('KANBAN.FETCH_ERROR'));
  } finally {
    if (requestGeneration === boardGeneration.value) loading[columnId] = false;
  }
};

const fetchColumns = async () => {
  try {
    const response = await KanbanColumnsApi.get();
    columnsMeta.value = response.data?.payload || [];
  } catch (error) {
    useAlert(t('KANBAN.FETCH_ERROR'));
  }
};

const fetchBoard = async () => {
  boardGeneration.value += 1;
  const generation = boardGeneration.value;
  isRefreshing.value = true;
  await fetchColumns();
  resetBuckets();
  await Promise.all(
    boardColumns.value.map(column => fetchColumn(column.id, { generation }))
  );
  if (generation === boardGeneration.value) isRefreshing.value = false;
};

const onColumnChange = async (columnId, event) => {
  const added = event.added;
  if (!added?.element) return;

  const conversation = added.element;
  const target = columnId === 'none' ? null : columnId;
  if (conversation.kanban_column_id === target) return;

  try {
    await ConversationApi.update({
      conversationId: conversation.id,
      kanbanColumnId: target,
    });
    conversation.kanban_column_id = target;
  } catch (error) {
    useAlert(t('KANBAN.UPDATE_ERROR'));
    await fetchBoard();
  }
};

const openCard = conversation => {
  contextMenu.value = null;
  selectedCard.value = conversation;
};

const openCardMenu = ({ conversation, event }) => {
  contextMenu.value = { conversation, x: event.clientX, y: event.clientY };
};

// Atualiza o card no board sem refazer todas as requisicoes.
const applyCardPatch = patch => {
  const alvo = selectedCard.value;
  if (!alvo) return;
  Object.keys(cards).forEach(columnId => {
    const item = cards[columnId].find(c => c.id === alvo.id);
    if (item) Object.assign(item, patch);
  });
  selectedCard.value = null;
};

// Tira o card do board sem refazer o fetch inteiro.
const removeCardFromBoard = conversationId => {
  const id = Number(conversationId);
  Object.keys(cards).forEach(columnId => {
    cards[columnId] = cards[columnId].filter(item => item.id !== id);
  });
  if (selectedCard.value?.id === id) selectedCard.value = null;
  if (contextMenu.value?.conversation?.id === id) contextMenu.value = null;
};

// A conversa pode ser excluida na tela de chat enquanto o board esta montado.
useEmitter(BUS_EVENTS.CONVERSATION_DELETED, removeCardFromBoard);

const cardPendingDelete = ref(null);
const deleteDialogRef = ref(null);

const askDeleteCard = conversation => {
  contextMenu.value = null;
  cardPendingDelete.value = conversation;
  deleteDialogRef.value?.open();
};

const confirmDeleteCard = async () => {
  const conversation = cardPendingDelete.value;
  if (!conversation) return;
  try {
    await store.dispatch('deleteConversation', conversation.id);
    removeCardFromBoard(conversation.id);
    useAlert(t('KANBAN.DELETE_CARD_SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.UPDATE_ERROR'));
  } finally {
    cardPendingDelete.value = null;
    deleteDialogRef.value?.close();
  }
};

const attendConversation = conversation => {
  contextMenu.value = null;
  router.push(
    frontendURL(
      conversationUrl({
        accountId: route.params.accountId,
        id: conversation.id,
      })
    )
  );
};

// Move pelo menu de contexto, sem precisar arrastar.
const moveCardTo = async (conversation, columnId) => {
  contextMenu.value = null;
  const target = columnId === 'none' ? null : columnId;
  if (conversation.kanban_column_id === target) return;
  try {
    await ConversationApi.update({
      conversationId: conversation.id,
      kanbanColumnId: target,
    });
    await fetchBoard();
  } catch (error) {
    useAlert(t('KANBAN.UPDATE_ERROR'));
  }
};

const openCreateForm = () => {
  editingColumn.value = null;
  isFormOpen.value = true;
};

const openEditForm = column => {
  editingColumn.value = column;
  isFormOpen.value = true;
};

const onFormSaved = async () => {
  isFormOpen.value = false;
  editingColumn.value = null;
  await fetchBoard();
};

const onDeleteColumn = async column => {
  try {
    await KanbanColumnsApi.delete(column.id);
    await fetchBoard();
  } catch (error) {
    useAlert(t('KANBAN.UPDATE_ERROR'));
  }
};

const moveColumn = async (column, offset) => {
  const ordered = [...columnsMeta.value];
  const from = ordered.findIndex(item => item.id === column.id);
  const to = from + offset;
  if (from < 0 || to < 0 || to >= ordered.length) return;

  const [moved] = ordered.splice(from, 1);
  ordered.splice(to, 0, moved);
  columnsMeta.value = ordered;

  try {
    await KanbanColumnsApi.reorder(
      ordered.map((item, index) => ({ id: item.id, position: index + 1 }))
    );
  } catch (error) {
    useAlert(t('KANBAN.UPDATE_ERROR'));
    await fetchBoard();
  }
};

watch([assigneeType, inboxId, teamId, label, sortBy], () => {
  fetchBoard();
});

onMounted(() => {
  fetchBoard();
});
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col bg-n-surface-1">
    <KanbanFilterBar
      v-model:assignee-type="assigneeType"
      v-model:inbox-id="inboxId"
      v-model:team-id="teamId"
      v-model:label="label"
      v-model:sort-by="sortBy"
      v-model:search-query="searchQuery"
      :is-refreshing="isRefreshing"
      @refresh="fetchBoard"
      @create-column="openCreateForm"
    />
    <div class="flex min-h-0 flex-1 gap-3 overflow-x-auto p-4">
      <KanbanColumn
        v-for="column in boardColumns"
        :key="column.id"
        :column="column"
        :conversations="cards[column.id] || []"
        :loading="loading[column.id] || isRefreshing"
        :has-more="hasMore[column.id]"
        :search-query="searchQuery"
        :is-first="column.id === boardColumns[1]?.id"
        :is-last="column.id === boardColumns[boardColumns.length - 1]?.id"
        @update:conversations="cards[column.id] = $event"
        @change="onColumnChange(column.id, $event)"
        @load-more="fetchColumn(column.id, { append: true })"
        @edit="openEditForm(column)"
        @delete="onDeleteColumn(column)"
        @move="moveColumn(column, $event)"
        @card-open="openCard"
        @card-menu="openCardMenu"
      />
    </div>
    <KanbanCardDialog
      v-if="selectedCard"
      :key="selectedCard.id"
      :conversation="selectedCard"
      @saved="applyCardPatch"
      @close="selectedCard = null"
    />
    <KanbanCardMenu
      v-if="contextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :columns="boardColumns"
      :current-column-id="contextMenu.conversation.kanban_column_id || 'none'"
      @open="openCard(contextMenu.conversation)"
      @note="openCard(contextMenu.conversation)"
      @attend="attendConversation(contextMenu.conversation)"
      @delete="askDeleteCard(contextMenu.conversation)"
      @move="moveCardTo(contextMenu.conversation, $event)"
      @close="contextMenu = null"
    />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('KANBAN.DELETE_CARD_CONFIRM_TITLE')"
      :description="$t('KANBAN.DELETE_CARD_CONFIRM_DESCRIPTION')"
      :confirm-button-label="$t('KANBAN.DELETE_CARD')"
      @confirm="confirmDeleteCard"
    />
    <KanbanColumnForm
      v-if="isFormOpen"
      :column="editingColumn"
      @saved="onFormSaved"
      @close="isFormOpen = false"
    />
  </div>
</template>
